import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { ContactCategory, ContactSubCategory } from 'src/Constants/ContactCategories.constants';
import {
  CompanyContactRole,
  ContactDtoFamilyRelationTypeEnum,
  ContactKind,
  CreateContactDto,
  CreateContactDtoTitleEnum,
  CreateMessagingChannelDtoTypeEnum,
  CreatePaymentMethodDtoTypeEnum,
  PaymentMode,
  UpsertInterlocutorDtoTitleEnum,
} from 'src/Services/API/generated';
import { validateEmailFormat } from 'src/Services/Validators/validateEmailFormat';
import { validateIBANFormat } from 'src/Services/Validators/validateIBANFormat';
import { validatePhoneNumberFormat } from 'src/Services/Validators/validatePhoneNumberFormat';

const sanitizeIBAN = (iban: string): string => {
  return iban
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
};

export interface InterlocutorFormValues {
  /** Stable React key, also for interlocutors not saved yet. */
  key: string;
  /** Set for an interlocutor already saved: the API updates it instead of creating one. */
  id?: string;
  title?: UpsertInterlocutorDtoTitleEnum;
  firstName: string;
  /** "Nom ou service" (e.g. "Service comptabilité"). */
  lastName: string;
  email: string;
  phoneNumber: string;
  companyRole: CompanyContactRole;
  companyRoleLabel: string;
}

export const emptyInterlocutor = (companyRole: CompanyContactRole): InterlocutorFormValues => ({
  key: crypto.randomUUID(),
  title: undefined,
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  companyRole,
  companyRoleLabel: '',
});

export const SIRET_PATTERN = /^\d{14}$/;
const stripSpaces = (value: string) => value.replace(/\s+/g, '');

export interface CreatePhysicalContactFormValues {
  addAnother: boolean;
  kind: ContactKind;
  /** The user picked Person / Company by hand: the category no longer presets it. */
  kindTouched: boolean;
  companyName: string;
  siret: string;
  /** The SIRET comes from the public register: shown read-only. */
  siretFound: boolean;
  websiteUrl: string;
  customerReference: string;
  usualPaymentMode?: PaymentMode;
  notes: string;
  interlocutors: InterlocutorFormValues[];
  title: CreateContactDtoTitleEnum;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  isEmergencyContact: boolean;
  messengerValue?: string;
  familyRelationType: ContactDtoFamilyRelationTypeEnum | undefined;
  imagePath?: string;
  imageUrl?: string;
  placeId: string;
  category?: ContactCategory;
  subcategory?: ContactSubCategory;
  messagingChannels: {
    SMS: boolean;
    EMAIL: boolean;
    SIGNAL: boolean;
    WHATSAPP: boolean;
    MESSENGER: boolean;
  };
  paymentMethods: Record<
    'iban' | 'lydia' | 'paypal' | 'wero',
    { identifier: string; isFavorite: boolean }
  >;
}

export const useCreatePhysicalContactForm = () => {
  const { t } = useTranslation();
  const { Sms, Email, Messenger, Signal, Whatsapp } = CreateMessagingChannelDtoTypeEnum;

  const form = useForm<
    CreatePhysicalContactFormValues,
    (value: CreatePhysicalContactFormValues) => CreateContactDto
  >({
    initialValues: {
      addAnother: false,
      kind: ContactKind.Person,
      kindTouched: false,
      companyName: '',
      siret: '',
      siretFound: false,
      websiteUrl: '',
      customerReference: '',
      usualPaymentMode: undefined as PaymentMode | undefined,
      notes: '',
      interlocutors: [] as InterlocutorFormValues[],
      title: CreateContactDtoTitleEnum.Mr,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      isEmergencyContact: false,
      messengerValue: '',
      familyRelationType: undefined as ContactDtoFamilyRelationTypeEnum | undefined,
      imagePath: undefined as string | undefined,
      imageUrl: undefined as string | undefined,
      placeId: '',
      category: undefined as ContactCategory | undefined,
      subcategory: undefined as ContactSubCategory | undefined,
      messagingChannels: {
        SMS: false,
        EMAIL: false,
        SIGNAL: false,
        WHATSAPP: false,
        MESSENGER: false,
      },
      paymentMethods: {
        iban: { identifier: '', isFavorite: false },
        lydia: { identifier: '', isFavorite: false },
        paypal: { identifier: '', isFavorite: false },
        wero: { identifier: '', isFavorite: false },
      },
    },
    validate: {
      firstName: (value: string, values) =>
        values.kind === ContactKind.Person && (value ?? '').length < 2
          ? t('contacts.invalidFirstName', 'Prénom invalide')
          : null,

      lastName: (value: string, values) =>
        values.kind === ContactKind.Person && (value ?? '').length < 2
          ? t('contacts.invalidName', 'Nom invalide')
          : null,

      title: (value: CreateContactDtoTitleEnum, values) =>
        values.kind === ContactKind.Person &&
        !Object.values(CreateContactDtoTitleEnum).includes(value)
          ? t('contacts.invalidTitle', 'Civilité invalide')
          : null,

      companyName: (value: string, values) =>
        values.kind === ContactKind.Company && !value.trim()
          ? t('contacts.company.requiredName', "Le nom de l'entreprise est obligatoire")
          : null,

      siret: (value: string, values) =>
        values.kind === ContactKind.Company && value && !SIRET_PATTERN.test(stripSpaces(value))
          ? t('contacts.company.invalidSiret', 'Le SIRET doit contenir 14 chiffres')
          : null,

      websiteUrl: (value: string, values) =>
        values.kind === ContactKind.Company && value && !/^\S+\.\S+$/.test(value.trim())
          ? t('contacts.company.invalidWebsite', 'Adresse de site invalide (ex. www.exemple.fr)')
          : null,

      interlocutors: {
        lastName: (value: string) =>
          !value.trim()
            ? t('contacts.company.requiredInterlocutorName', 'Indiquez un nom ou un service')
            : null,
        companyRoleLabel: (value: string, values, path) => {
          const index = Number(path.split('.')[1]);
          return values.interlocutors[index]?.companyRole === CompanyContactRole.Other &&
            !value.trim()
            ? t('contacts.company.requiredRoleLabel', 'Précisez le rôle')
            : null;
        },
        email: (value: string) =>
          value && !validateEmailFormat(value)
            ? t('contacts.invalidEmail', 'Email invalide')
            : null,
        phoneNumber: (value: string) =>
          value && !validatePhoneNumberFormat(value)
            ? t('contacts.invalidPhoneNumber', 'Numéro de téléphone invalide')
            : null,
      },

      email: (value?: string) => {
        if (form.values.messagingChannels.EMAIL && !value) {
          return t('contacts.requiredEmail', 'Email obligatoire');
        }
        return value && !validateEmailFormat(value)
          ? t('contacts.invalidEmail', 'Email invalide')
          : null;
      },
      phoneNumber: (value: string | undefined, values) => {
        const needPhone = ['SMS', 'WHATSAPP', 'SIGNAL'].some(
          ch => values.messagingChannels[ch as CreateMessagingChannelDtoTypeEnum]
        );
        if (needPhone && !value) {
          return t('contacts.requiredPhone', 'Numéro de téléphone obligatoire');
        }
        return value && !validatePhoneNumberFormat(value)
          ? t('contacts.invalidPhoneNumber', 'Numéro de téléphone invalide')
          : null;
      },
      messengerValue: (value?: string) => {
        const messengerIsChecked = form.values.messagingChannels.MESSENGER;
        const isValidMessenger = value && value.length > 2;
        if (messengerIsChecked && !isValidMessenger) {
          return t('contacts.invalidMessenger', 'Profil messenger invalide');
        }
        return null;
      },
      paymentMethods: {
        iban: {
          identifier: (value: string, values) => {
            if (value) {
              const isValidIBAN = validateIBANFormat(value);
              return isValidIBAN ? null : t('contacts.invalidIBAN', 'IBAN invalide');
            }
            if (values.paymentMethods.iban.isFavorite)
              return t('contacts.missingIBAN', 'IBAN manquant');
            return null;
          },
        },
        lydia: {
          identifier: (value: string, values) => {
            if (value) {
              const isValidPhoneNumber = validatePhoneNumberFormat(value);
              return isValidPhoneNumber
                ? null
                : t('contacts.invalidPhoneNumber', 'Numéro de téléphone invalide');
            }
            if (values.paymentMethods.lydia.isFavorite)
              return t('contacts.missingPhoneNumber', 'Téléphone manquant');
            return null;
          },
        },
        paypal: {
          identifier: (value: string, values) => {
            if (value) {
              const isValidEmail = validateEmailFormat(value);
              return isValidEmail ? null : t('contacts.invalidEmail', 'Email invalide');
            }
            if (values.paymentMethods.paypal.isFavorite)
              return t('contacts.missingPhoneNumber', 'Email manquant');
            return null;
          },
        },
        wero: {
          identifier: (value: string, values) => {
            if (value) {
              const isValidPhoneNumber = validatePhoneNumberFormat(value);
              return isValidPhoneNumber
                ? null
                : t('contacts.invalidPhoneNumber', 'Numéro de téléphone invalide');
            }
            if (values.paymentMethods.wero.isFavorite)
              return t('contacts.missingPhoneNumber', 'Téléphone manquant');
            return null;
          },
        },
      },
    },
    transformValues: values => {
      const {
        messengerValue,
        paymentMethods,
        addAnother: _addAnother,
        kindTouched: _kindTouched,
        siretFound: _siretFound,
        companyName,
        siret,
        websiteUrl,
        customerReference,
        notes,
        interlocutors,
        ...othersValues
      } = values;
      const { messagingChannels, phoneNumber, email } = othersValues;

      const channels = [
        { type: Sms, identifier: null, condition: messagingChannels.SMS },
        { type: Email, identifier: null, condition: messagingChannels.EMAIL },
        { type: Signal, identifier: null, condition: messagingChannels.SIGNAL },
        { type: Whatsapp, identifier: null, condition: messagingChannels.WHATSAPP },
        { type: Messenger, identifier: messengerValue, condition: messagingChannels.MESSENGER },
      ]
        .filter(channel => channel.condition)
        .map(channel => ({ type: channel.type, identifier: channel.identifier }));

      const formattedPaymentMethods = Object.entries(paymentMethods)
        .map(([method, props]) => ({
          type: method.toUpperCase() as CreatePaymentMethodDtoTypeEnum,
          identifier: props.identifier,
          isFavorite: props.isFavorite,
        }))
        .filter(({ identifier }) => identifier)
        .map(method =>
          method.type === 'IBAN'
            ? { ...method, identifier: sanitizeIBAN(method.identifier) }
            : method
        );

      const common = {
        ...othersValues,
        paymentMethods: formattedPaymentMethods,
        messagingChannels: channels,
        phoneNumber: phoneNumber || null,
        email: email || null,
      };

      if (values.kind === ContactKind.Person) {
        // Interlocutors left undefined: the API leaves them untouched.
        return common;
      }

      const websiteTrimmed = websiteUrl.trim();
      return {
        ...common,
        title: undefined,
        firstName: undefined,
        lastName: undefined,
        companyName: companyName.trim(),
        siret: siret ? stripSpaces(siret) : undefined,
        websiteUrl: websiteTrimmed
          ? /^https?:\/\//i.test(websiteTrimmed)
            ? websiteTrimmed
            : `https://${websiteTrimmed}`
          : undefined,
        customerReference: customerReference.trim() || undefined,
        notes: notes.trim() || undefined,
        interlocutors: interlocutors.map(interlocutor => ({
          id: interlocutor.id,
          title: interlocutor.title,
          firstName: interlocutor.firstName.trim() || undefined,
          lastName: interlocutor.lastName.trim(),
          email: interlocutor.email.trim() || undefined,
          phoneNumber: interlocutor.phoneNumber.trim() || undefined,
          companyRole: interlocutor.companyRole,
          companyRoleLabel:
            interlocutor.companyRole === CompanyContactRole.Other
              ? interlocutor.companyRoleLabel.trim()
              : undefined,
        })),
      };
    },
  });

  return { form };
};
